import { useState, useCallback } from "react";
import { cleanRut, validateRut, validateTelefono, validateEmail } from "../shared/validations";
import * as clienteService from "../../../services/ClienteService";

const codigosPaises = [
  { label: "Chile (+56)", value: "+56" },
  { label: "Argentina (+54)", value: "+54" },
  { label: "Perú (+51)", value: "+51" },
];

const useRepresentanteForm = (baseUrl, toast) => {
  const [nuevoNombreRepresentante, setNuevoNombreRepresentante] = useState("");
  const [nuevoRutRepresentante, setNuevoRutRepresentante] = useState("");
  const [nuevoTelefonoRepresentante, setNuevoTelefonoRepresentante] = useState("");
  const [nuevoCorreoRepresentante, setNuevoCorreoRepresentante] = useState("");
  const [nuevoRepresentantes, setNuevoRepresentantes] = useState([]);
  const [isValidRepresentante, setIsValidRepresentante] = useState(false);
  const [codigoPais, setCodigoPais] = useState("+56");
  const [indiceEdicionRepresentante, setIndiceEdicionRepresentante] = useState(null);
  const [idRepresentante, setIdRepresentante] = useState(null);
  const [erroresRepresentante, setErroresRepresentante] = useState({
    nombre: false,
    rut: false,
    telefono: false,
    correo: false,
  });

  const resetFormularioRepresentante = useCallback(() => {
    setNuevoNombreRepresentante("");
    setNuevoRutRepresentante("");
    setNuevoTelefonoRepresentante("");
    setNuevoCorreoRepresentante("");
    setIndiceEdicionRepresentante(null);
  }, []);

  const handleRutRepresentanteChange = (event) => {
    const inputRut = event.target.value;
    setNuevoRutRepresentante(inputRut);
    setIsValidRepresentante(validateRut(cleanRut(inputRut)));
  };

  const handleRutRepresentanteBlur = async () => {
    if (isValidRepresentante) {
      let rutLimpio = nuevoRutRepresentante.replace(/[.\s]/g, "");
      let rutFormateado = nuevoRutRepresentante;
      if (!/-/.test(nuevoRutRepresentante)) {
        rutFormateado = rutLimpio.slice(0, -1) + "-" + rutLimpio.slice(-1);
      }
      setNuevoRutRepresentante(rutFormateado);
      try {
        const data = await clienteService.getRepresentanteByRut(baseUrl, rutFormateado);
        if (data) {
          setNuevoNombreRepresentante(data.nombre || "");
          setNuevoCorreoRepresentante(data.correo || "");
          const telefono = data.telefono ? data.telefono.replace(/^[+\d\s()-]+/, "") : "";
          setNuevoTelefonoRepresentante(telefono);
          if (data.telefono && data.telefono.startsWith("+56")) {
            setCodigoPais("+56");
          }
        }
      } catch (error) {
        console.error("Error al buscar representante:", error);
      }
    }
  };

  const handleTelefonoRepresentanteChange = (e) => {
    setNuevoTelefonoRepresentante(e.target.value.replace(/[^0-9\s()+-]/g, ""));
  };

  const handleCorreoRepresentanteChange = (e) => {
    setNuevoCorreoRepresentante(e.target.value.replace(/\s/g, ""));
  };

  const handleGuardarRepresentante = () => {
    const nuevosErrores = {
      nombre: !nuevoNombreRepresentante.trim(),
      telefono: !nuevoTelefonoRepresentante.trim() || !validateTelefono(nuevoTelefonoRepresentante),
      correo: !nuevoCorreoRepresentante.trim() || !validateEmail(nuevoCorreoRepresentante),
    };

    setErroresRepresentante(nuevosErrores);

    if (Object.values(nuevosErrores).some((error) => error)) {
      toast.current?.show({
        severity: "error",
        summary: "Error de Validación",
        detail: "Por favor, complete correctamente todos los campos",
        life: 3000,
      });
      return;
    }

    if (indiceEdicionRepresentante !== null) {
      const actualizados = [...nuevoRepresentantes];
      actualizados[indiceEdicionRepresentante] = {
        id: idRepresentante,
        nombre: nuevoNombreRepresentante,
        rut: nuevoRutRepresentante,
        telefono: `${codigoPais} ${nuevoTelefonoRepresentante}`,
        correo: nuevoCorreoRepresentante,
      };
      setNuevoRepresentantes(actualizados);
      setIndiceEdicionRepresentante(null);
      resetFormularioRepresentante();
    } else {
      handleAgregarNuevoRepresentante();
    }
  };

  const handleAgregarNuevoRepresentante = () => {
    if (nuevoRepresentantes.some((rep) => rep.rut.trim().toUpperCase() === nuevoRutRepresentante.trim().toUpperCase())) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "El representante con este RUT ya existe",
        life: 3000,
      });
      return;
    }

    const representante = {
      rut: nuevoRutRepresentante,
      nombre: nuevoNombreRepresentante,
      correo: nuevoCorreoRepresentante,
      telefono: `${codigoPais} ${nuevoTelefonoRepresentante}`,
    };

    setNuevoRepresentantes([...nuevoRepresentantes, representante]);
    resetFormularioRepresentante();
  };

  const handleEliminarRepresentante = (rowIndex) => {
    const updated = [...nuevoRepresentantes];
    updated.splice(rowIndex, 1);
    setNuevoRepresentantes(updated);
  };

  const handleDesactivarRepresentante = async (rowIndex) => {
    const representante = nuevoRepresentantes[rowIndex];
    if (!representante || !representante.id) {
      toast.current?.show({
        severity: "warn",
        summary: "ID inválido",
        detail: "No se encontró el ID del representante a desactivar.",
        life: 3000,
      });
      return;
    }
    try {
      await clienteService.cambiarEstadoRepresentante(baseUrl, representante.id, 0);
      setNuevoRepresentantes((prev) => prev.filter((_, idx) => idx !== rowIndex));
      toast.current?.show({
        severity: "success",
        summary: "Representante Desactivado",
        detail: "El representante ha sido desactivado con éxito.",
        life: 3000,
      });
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "No se pudo desactivar el representante. Debe tener al menos un registro activo.",
        life: 3000,
      });
    }
  };

  const handleSeleccionarRepresentante = (index) => {
    const rep = nuevoRepresentantes[index];
    setIdRepresentante(rep.id);
    setNuevoNombreRepresentante(rep.nombre);
    setNuevoRutRepresentante(rep.rut);
    setNuevoTelefonoRepresentante(rep.telefono.replace(/^\+\d+\s*/, ""));
    setNuevoCorreoRepresentante(rep.correo);
    setIndiceEdicionRepresentante(index);
  };

  const setRepresentantesIniciales = (representantes) => {
    if (Array.isArray(representantes) && representantes.length > 0) {
      setNuevoRepresentantes(representantes);
    }
  };

  return {
    nuevoNombreRepresentante,
    setNuevoNombreRepresentante,
    nuevoRutRepresentante,
    nuevoTelefonoRepresentante,
    nuevoCorreoRepresentante,
    nuevoRepresentantes,
    setNuevoRepresentantes,
    isValidRepresentante,
    codigoPais,
    setCodigoPais,
    indiceEdicionRepresentante,
    erroresRepresentante,
    codigosPaises,
    handleRutRepresentanteChange,
    handleRutRepresentanteBlur,
    handleTelefonoRepresentanteChange,
    handleCorreoRepresentanteChange,
    handleGuardarRepresentante,
    handleEliminarRepresentante,
    handleDesactivarRepresentante,
    handleSeleccionarRepresentante,
    resetFormularioRepresentante,
    setRepresentantesIniciales,
  };
};

export default useRepresentanteForm;
